import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { contactInfo } from '../mock';
import { MapPin, Mail, Phone, ArrowRight, Navigation } from 'lucide-react';
import { Button } from './ui/button';

// Echte OSM-Koordinaten für Bundeskanzlerplatz 2D, 53113 Bonn
const LAT = 50.7187702;
const LNG = 7.1147981;
const DESTINATION = encodeURIComponent(contactInfo.address);

const openNavigation = () => {
  if (typeof window === 'undefined') return;
  const ua = window.navigator.userAgent || '';
  const platform = window.navigator.platform || '';
  const isAppleDevice =
    /iPhone|iPad|iPod/i.test(ua) ||
    /Mac/i.test(platform) ||
    (platform === 'MacIntel' && (window.navigator.maxTouchPoints || 0) > 1);
  const url = isAppleDevice
    ? `https://maps.apple.com/?daddr=${LAT},${LNG}&q=${DESTINATION}`
    : `https://www.google.com/maps/dir/?api=1&destination=${LAT},${LNG}&destination_place_id=${DESTINATION}`;
  window.open(url, '_blank', 'noopener,noreferrer');
};

const Contact = () => {
  const { t } = useTranslation();
  return (
    <section id="kontakt" className="bg-white py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <h2 className="font-serif font-light text-[#1B3A5C] text-4xl md:text-5xl lg:text-6xl tracking-tight mb-16">
          {t('contact.headline', 'KONTAKT')}
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <h3 className="text-[#1B3A5C] font-semibold tracking-[0.2em] text-sm mb-6">
              {t('contact.address_label', 'UNSERE ADRESSE')}
            </h3>

            <div className="space-y-5">
              <div className="flex items-start gap-4 p-5 bg-[#f5f8fa] border-l-4 border-[#b8893d]">
                <MapPin className="w-5 h-5 text-[#b8893d] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[#1B3A5C] font-medium">{contactInfo.address}</p>
                  <button
                    type="button"
                    onClick={openNavigation}
                    data-testid="open-navigation-btn"
                    className="inline-flex items-center gap-1.5 text-[#b8893d] text-sm mt-2 hover:gap-2 transition-all"
                  >
                    <Navigation className="w-3.5 h-3.5" />
                    {t('contact.directions_cta', 'Route planen (Google / Apple Maps)')}
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              <a
                href={`mailto:${contactInfo.email}`}
                className="flex items-start gap-4 p-5 bg-[#f5f8fa] border-l-4 border-[#b8893d] hover:bg-[#eaf1f5] transition-colors"
              >
                <Mail className="w-5 h-5 text-[#b8893d] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[#34536b] text-xs uppercase tracking-wider mb-1">{t('contact.email_label', 'E-Mail')}</p>
                  <p className="text-[#1B3A5C] font-medium">{contactInfo.email}</p>
                </div>
              </a>

              <a
                href={`tel:${contactInfo.phone}`}
                className="flex items-start gap-4 p-5 bg-[#f5f8fa] border-l-4 border-[#b8893d] hover:bg-[#eaf1f5] transition-colors"
              >
                <Phone className="w-5 h-5 text-[#b8893d] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[#34536b] text-xs uppercase tracking-wider mb-1">{t('contact.phone_label', 'Telefon')}</p>
                  <p className="text-[#1B3A5C] font-medium">{contactInfo.phone}</p>
                </div>
              </a>
            </div>

            <div className="mt-8 p-6 bg-[#1B3A5C] text-white rounded-sm">
              <p className="text-sm leading-relaxed mb-4">
                {t('contact.register_hint', 'Bitte registrieren Sie sich unter dem Menüpunkt Mandantenerfassung, falls Sie noch kein Mandant unserer Kanzlei sind.')}
              </p>
              <p className="text-white/70 text-xs mb-4">
                {t('contact.privacy_hint_pre', 'Informationen zur Verarbeitung Ihrer Daten finden Sie in unserer')}{' '}
                <Link to="/datenschutz" className="text-[#e0b56a] underline">{t('contact.privacy_link', 'Datenschutzerklärung')}</Link>.
              </p>
              <Link to="/mandantenerfassung">
                <Button className="bg-[#b8893d] hover:bg-[#a07730] text-white tracking-[0.15em] text-xs">
                  {t('contact.cta', 'MANDANTENERFASSUNG')}
                  <ArrowRight className="ml-2 w-3.5 h-3.5" />
                </Button>
              </Link>
            </div>
          </div>

          <div id="karte" className="relative">
            <div className="w-full h-[500px] rounded-sm overflow-hidden shadow-lg relative group">
              <iframe
                title={t('contact.map_title', 'Kanzlei Standort')}
                src={`https://www.google.com/maps?q=${LAT},${LNG}&hl=de&z=16&output=embed`}
                className="w-full h-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
              <button
                type="button"
                onClick={openNavigation}
                data-testid="map-overlay-navigate-btn"
                className="absolute top-3 right-3 bg-white/95 hover:bg-white text-[#1B3A5C] px-3.5 py-2 rounded-sm shadow-lg flex items-center gap-2 text-xs tracking-wide font-medium transition-transform hover:-translate-y-0.5"
              >
                <Navigation className="w-3.5 h-3.5 text-[#b8893d]" />
                {t('contact.directions_button', 'NAVIGATION STARTEN')}
              </button>
            </div>
            <div className="absolute -bottom-4 -right-4 bg-[#b8893d] text-white px-6 py-3 shadow-lg hidden md:block">
              <p className="text-xs tracking-[0.2em]">{t('contact.city_badge', 'BONN, DEUTSCHLAND')}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
